'use client';
import {FC} from 'react';
import useSlickState from '@hooks/useSlickState';

import MuiBox from '@new-components/atoms/MuiBox';
import MuiContainer from '@new-components/atoms/MuiContainer';
import MuiIconButton from '@new-components/atoms/MuiIconButton';
import MuiTypography from '@new-components/atoms/MuiTypography';
import UsefulResourceCard from '@new-components/atoms/UsefulResourceCard';
import {ResourceCarousel} from '@new-components/organisms/UsefulResourceBlock/styles';
import {
  NextArrow,
  PrevArrow,
} from '@new-components/organisms/UsefulResourceBlock/icons';

import type {UsefulResourceCardProps} from '@new-components/atoms/UsefulResourceCard';
import {Settings} from 'react-slick';

const carouselSettings: Settings = {
  dots: false,
  arrows: false,
  infinite: false,
  autoplay: false,
  speed: 500,
  slidesToShow: 3,
  slidesToScroll: 3,
  rows: 2,
  slidesPerRow: 1,
  swipeToSlide: false,
  swipe: false,
  responsive: [
    {
      breakpoint: 900,
      settings: {
        slidesToShow: 1,
        rows: 3,
        slidesToScroll: 1,
        centerMode: true,
        swipeToSlide: true,
        centerPadding: '16px',
        swipe: true,
      },
    },
  ],
};

export interface UsefulResourceBlockProps {
  title: string;
  resources: Array<UsefulResourceCardProps>;
}

const UsefulResourceBlock: FC<UsefulResourceBlockProps> = (props) => {
  if (!props.resources?.length) return null;

  const {sliderRef, values, mutations} = useSlickState(
    props.resources.length,
    2,
    3
  );

  return (
    <MuiBox sx={{py: {xs: '64px', md: '160px'}, background: '#2D2D2D'}}>
      <MuiContainer sx={{pr: {xs: 0, md: '32px'}}}>
        <MuiBox
          sx={{
            flex: 1,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
          <MuiBox
            sx={{
              maxWidth: {md: '453px'},
              textAlign: 'left',
            }}>
            <MuiTypography
              sx={{
                fontWeight: {xs: 400, md: 300},
                fontSize: {xs: '24px', md: '48px'},
                lineHeight: {xs: '30px', md: '60px'},
                letterSpacing: {xs: '-0.4px', md: '-0.8px'},
                color: '#fff',
                textAlign: 'left',
                marginBottom: {xs: '12px', md: '24px'},
              }}>
              {props.title}
            </MuiTypography>
          </MuiBox>
          {props.resources.length > 6 && (
            <MuiBox
              sx={{
                display: {xs: 'none', md: 'flex'},
                gap: 2,
              }}>
              <MuiIconButton
                disabled={values.isEnabled.prev}
                onClick={mutations.handlePrev}
                data-testid='fcc-prev-button'
                sx={{
                  'width': '40px',
                  'height': '40px',
                  '&.Mui-disabled': {
                    opacity: 0.5,
                  },
                }}>
                <PrevArrow />
              </MuiIconButton>
              <MuiIconButton
                disabled={values.isEnabled.next}
                onClick={mutations.handleNext}
                data-testid='fcc-next-button'
                sx={{
                  'width': '40px',
                  'height': '40px',
                  '&.Mui-disabled': {
                    opacity: 0.5,
                  },
                }}>
                <NextArrow />
              </MuiIconButton>
            </MuiBox>
          )}
        </MuiBox>
        <ResourceCarousel
          ref={sliderRef}
          beforeChange={mutations.handleBeforeChange}
          {...carouselSettings}>
          {props.resources.map((resourceProps) => (
            <UsefulResourceCard key={resourceProps.title} {...resourceProps} />
          ))}
        </ResourceCarousel>
      </MuiContainer>
    </MuiBox>
  );
};

export default UsefulResourceBlock;
